import { PropsWithChildren, useRef } from "react";
import { ControllerRenderProps, FieldPath, FieldValues } from "react-hook-form";
import Select from "react-select";
import { SelectOption, TextAction, translate } from "@shape/reef-shared";
import { BaseField, BaseFieldProps } from "./BaseField";
import { MultiValueTag } from "./MultiValueTag";
import styles from "./MultipleSelect.module.scss";

export interface MultipleSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends BaseFieldProps<TFieldValues, TName> {
  options: SelectOption[];
  placeholder?: string;
  defaultValue?: (string | number)[] | null;
  isListInsideSelect?: boolean;
  onChange?: (selectedOptions: SelectOption[]) => void;
  onFilterChange?: (currentFilter: string) => void;
}

interface ReactSelectOption {
  value: string | number;
  label: string;
}

const getSelectOptions = (options: SelectOption[]): ReactSelectOption[] =>
  options.map(
    (option: SelectOption): ReactSelectOption => ({
      value: option.id,
      label: option.label,
    })
  );

const resolveSelectOption = (item: ReactSelectOption, options: SelectOption[]): SelectOption =>
  options.find((option) => option.id === item.value) ?? { id: item.value, label: item.label };

const toSelectOptions = (
  selected: readonly ReactSelectOption[] | null,
  options: SelectOption[]
): SelectOption[] => (selected ?? []).map((item) => resolveSelectOption(item, options));

const selectClassNames = {
  control: (state: { isFocused: boolean }) =>
    `${styles.control} ${state.isFocused ? styles.controlFocused : ""}`,
  valueContainer: () => styles.valueContainer,
  placeholder: () => styles.placeholder,
  input: () => styles.input,
  indicatorSeparator: () => styles.indicatorSeparator,
  dropdownIndicator: () => styles.dropdownIndicator,
  menu: () => styles.menu,
  menuList: () => styles.menuList,
  option: (state: { isFocused: boolean }) =>
    `${styles.option} ${state.isFocused ? styles.optionFocused : ""}`,
  noOptionsMessage: () => styles.noOptionsMessage,
};

interface MultipleSelectContentProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> {
  field: ControllerRenderProps<TFieldValues, TName>;
  options: SelectOption[];
  placeholder?: string;
  readOnly?: boolean;
  onChange?: (selectedOptions: SelectOption[]) => void;
  onFilterChange?: (currentFilter: string) => void;
  isListInsideSelect?: boolean;
}

function MultipleSelectContent<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  field,
  options,
  placeholder,
  readOnly,
  onChange: customOnChange,
  onFilterChange,
  isListInsideSelect,
}: MultipleSelectContentProps<TFieldValues, TName>) {
  const knownOptionsRef = useRef<Map<string | number, SelectOption>>(new Map());
  options.forEach((option) => knownOptionsRef.current.set(option.id, option));

  const selectedIds: (string | number)[] = Array.isArray(field.value) ? field.value : [];
  const selectedOptions = selectedIds
    .map((id) => knownOptionsRef.current.get(id))
    .filter((option): option is SelectOption => option !== undefined);

  const updateSelection = (newSelectedOptions: SelectOption[]) => {
    newSelectedOptions.forEach((option) => knownOptionsRef.current.set(option.id, option));
    field.onChange(newSelectedOptions.map((option) => option.id));
    customOnChange?.(newSelectedOptions);
  };

  const removeOption: (optionId: string | number) => void = (optionId) => {
    updateSelection(selectedOptions.filter((selected) => selected.id !== optionId));
  };

  return (
    <div>
      {!readOnly && (
        <Select<ReactSelectOption, true>
          isMulti
          unstyled
          isClearable={isListInsideSelect}
          options={getSelectOptions(options)}
          value={getSelectOptions(selectedOptions)}
          onChange={(selected) => updateSelection(toSelectOptions(selected, options))}
          onInputChange={(newValue, { action }) => {
            if (action === "input-change") {
              onFilterChange?.(newValue);
            }
          }}
          placeholder={placeholder ?? translate("generic.selectAnOption")}
          components={{
            MultiValue: isListInsideSelect
              ? ({ data }) => (
                  <MultiValueTag
                    key={data.value}
                    label={data.label}
                    onRemove={readOnly ? undefined : () => removeOption(data.value)}
                  />
                )
              : () => null,
          }}
          closeMenuOnSelect={false}
          classNames={selectClassNames}
        />
      )}

      {(!isListInsideSelect || readOnly) && (
        <div className={styles.selectedList}>
          {selectedOptions.map((option) => (
            <MultiValueTag
              key={option.id}
              label={option.label}
              onRemove={readOnly ? undefined : () => removeOption(option.id)}
            />
          ))}
          {readOnly && !selectedOptions.length && "-"}
          {!readOnly && selectedOptions.length > 0 && (
            <TextAction
              type="button"
              className={styles.clearAll}
              onClick={() => updateSelection([])}
            >
              {translate("formGeneric.clearAll")}
            </TextAction>
          )}
        </div>
      )}
    </div>
  );
}

export function MultipleSelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  name,
  label,
  subtitle,
  form,
  defaultValue,
  rules,
  options,
  placeholder,
  readOnly,
  fieldClassName,
  labelInfo,
  isListInsideSelect,
  onChange: customOnChange,
  onFilterChange,
}: PropsWithChildren<MultipleSelectProps<TFieldValues, TName>>) {
  return (
    <BaseField
      form={form}
      name={name}
      label={label}
      subtitle={subtitle}
      defaultValue={defaultValue}
      rules={rules}
      fieldClassName={fieldClassName}
      labelInfo={labelInfo}
      readOnly={readOnly}
      render={({ field }) => (
        <MultipleSelectContent
          field={field}
          options={options}
          placeholder={placeholder}
          readOnly={readOnly}
          onChange={customOnChange}
          onFilterChange={onFilterChange}
          isListInsideSelect={isListInsideSelect}
        />
      )}
    />
  );
}
